import React from 'react';
import SparkMD5 from 'spark-md5';
import API_BASE_URL from './config/api';

/**
 * Blocks a photo from being submitted for two different units.
 *
 * We MD5 the exact bytes we're about to upload and ask the backend whether
 * they've been seen before. MD5 rather than the native SubtleCrypto digests
 * because Back4App keeps files on S3, where a single-part upload's ETag *is*
 * the content MD5 — so historical photos can be indexed with a HEAD request
 * instead of a download, and new uploads share one hash space with them.
 *
 * Everything here fails open. A tech standing in a stairwell on one bar must
 * never be unable to finish a unit because a dedupe lookup timed out, so any
 * error means "not a duplicate" and the upload proceeds.
 */

// Unit-level context (building, unit, tech). Provided by the add-unit form,
// which is what enables the check: components rendered outside a provider —
// the work order form, for instance — are left alone entirely.
export const PhotoDedupeContext = React.createContext(null);

export const usePhotoDedupe = () => React.useContext(PhotoDedupeContext);

// Big enough that hashing stays quick on a mid-range phone while still
// covering the whole file for anything the camera produces after compression.
const CHUNK_BYTES = 2 * 1024 * 1024;

const LOOKUP_TIMEOUT_MS = 6000;

/** MD5 of a File/Blob, read in chunks so a large photo can't spike memory. */
export async function hashFile(file) {
  if (!file) return '';
  try {
    const spark = new SparkMD5.ArrayBuffer();
    for (let offset = 0; offset < file.size; offset += CHUNK_BYTES) {
      const slice = file.slice(offset, Math.min(offset + CHUNK_BYTES, file.size));
      spark.append(await slice.arrayBuffer());
    }
    return spark.end();
  } catch (err) {
    console.warn('[photoDedupe] hashing failed, skipping the check:', err);
    return '';
  }
}

async function postJson(path, body, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Where else this exact image has been submitted.
 * Resolves to null when it's clean, unknown, or we couldn't find out.
 */
export async function checkPhotoHash(hash, context, size) {
  if (!hash || !context) return null;
  try {
    const data = await postJson('/api/field/photos/check', {
      hash,
      size,
      buildingId: context.buildingId || '',
      unitId: context.unitId || '',
      unitNumber: context.unitNumber || '',
    }, LOOKUP_TIMEOUT_MS);
    return data && data.duplicate ? data.usage || {} : null;
  } catch (err) {
    console.warn('[photoDedupe] check unavailable, allowing upload:', err);
    return null;
  }
}

/** Record an uploaded photo so later uploads can match against it. */
export async function registerPhotoHash(hash, context, { url, size, fieldName, fieldLabel }) {
  if (!hash || !context) return;
  try {
    await postJson('/api/field/photos/register', {
      hash,
      url,
      size,
      fieldName: fieldName || '',
      fieldLabel: fieldLabel || '',
      buildingId: context.buildingId || '',
      buildingName: context.buildingName || '',
      unitId: context.unitId || '',
      unitNumber: context.unitNumber || '',
      maintenanceID: context.maintenanceID || '',
      workerName: context.workerName || '',
    }, LOOKUP_TIMEOUT_MS);
  } catch (err) {
    // Costs us one photo's worth of coverage; not worth surfacing to the tech.
    console.warn('[photoDedupe] register failed:', err);
  }
}

/** Human-readable reason a photo was rejected. */
export function duplicateMessage(usage) {
  const unit = usage?.unitNumber ? `unit ${usage.unitNumber}` : 'another unit';
  const where = usage?.buildingName ? ` at ${usage.buildingName}` : '';
  const slot = usage?.field ? ` for the ${usage.field} photo` : '';
  return `This photo has already been used${slot} on ${unit}${where}. `
    + `Please take a new photo of this unit.`;
}
